import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import Pagination from '@/components/base/Pagination';
import usePageMeta from '@/hooks/usePageMeta';
import { news } from '@/utils/data';

const PAGE_SIZE = 5;

const CATEGORIES = [
  { key: 'all', label: '全部资讯' },
  { key: 'movie', label: '电影新闻' },
  { key: 'tv', label: '电视剧新闻' },
  { key: 'review', label: '影评专题' },
];

export default function NewsPage() {
  const [category, setCategory] = useState('all');
  const [page, setPage] = useState(1);

  const filtered = useMemo(
    () => [...news].filter((n) => category === 'all' || n.category === category),
    [category],
  );

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const visible = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const [lead, ...rest] = visible;

  const changeCategory = (key: string) => {
    setCategory(key);
    setPage(1);
  };

  usePageMeta({
    title: '最新影视资讯 - 御宅屋',
    description: '御宅屋影视资讯频道，汇集电影、电视剧最新动态、定档消息与深度影评，帮你紧跟影视圈每一个热点。',
    keywords: '影视资讯,电影新闻,电视剧新闻,影评,御宅屋资讯',
  });

  return (
    <div className="w-full px-4 md:px-8 pb-16">
      <div className="pt-2">
        <Breadcrumb items={[{ label: '影视资讯' }]} />
      </div>

      <div className="rounded-lg bg-gradient-to-r from-primary-900/60 to-background-100 border border-background-200/70 p-6 md:p-8 mb-8">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
          最新影视资讯
        </h1>
        <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-3xl leading-relaxed">
          汇集电影、电视剧的最新动态与深度影评，从定档消息到幕后专访，帮你紧跟影视圈每一个热点。
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {CATEGORIES.map((c) => (
          <button
            key={c.key}
            type="button"
            onClick={() => changeCategory(c.key)}
            className={`px-4 py-2 rounded-full text-sm border transition-colors cursor-pointer whitespace-nowrap ${
              category === c.key
                ? 'bg-accent-500 text-background-50 border-accent-500 font-medium'
                : 'border-background-300/60 text-foreground-700 hover:border-accent-500/50 hover:text-accent-400'
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {visible.length > 0 ? (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            {lead && (
              <Link
                to={`/news/${lead.id}`}
                className="group lg:col-span-2 relative rounded-lg overflow-hidden border border-background-200/70 min-h-[300px] md:min-h-[380px] flex"
              >
                <img
                  src={lead.cover}
                  alt={lead.title}
                  title={`${lead.title} 影视资讯`}
                  className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/60 to-transparent"></div>
                <div className="relative z-10 mt-auto p-6">
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 line-clamp-2 group-hover:text-accent-400 transition-colors">
                    {lead.title}
                  </h2>
                  <p className="mt-3 text-sm text-foreground-600 line-clamp-2">{lead.summary}</p>
                  <p className="mt-3 text-xs text-foreground-500">
                    <i className="ri-time-line mr-1"></i>
                    {lead.publishedAt} · {lead.author}
                  </p>
                </div>
              </Link>
            )}

            <div className="flex flex-col gap-3">
              {rest.map((item) => (
                <Link
                  key={item.id}
                  to={`/news/${item.id}`}
                  className="group flex items-center gap-4 p-3.5 rounded-lg bg-background-100 border border-background-200/70 hover:border-accent-500/50 transition-colors"
                >
                  <div className="w-24 h-16 shrink-0 rounded-md overflow-hidden bg-background-200">
                    <img
                      src={item.cover}
                      alt={item.title}
                      title={`${item.title} 影视资讯`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm font-medium text-foreground-950 line-clamp-2 group-hover:text-accent-400 transition-colors">
                      {item.title}
                    </h3>
                    <p className="mt-1 text-xs text-foreground-500">
                      <i className="ri-time-line mr-1"></i>
                      {item.publishedAt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </>
      ) : (
        <div className="flex flex-col items-center justify-center py-20 rounded-lg bg-background-100 border border-background-200/70">
          <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200/70 text-foreground-500">
            <i className="ri-newspaper-line text-3xl"></i>
          </span>
          <h3 className="mt-5 font-heading text-lg font-semibold text-foreground-900">
            该分类下暂无资讯
          </h3>
          <button
            type="button"
            onClick={() => changeCategory('all')}
            className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            查看全部资讯
          </button>
        </div>
      )}
    </div>
  );
}